const STORAGE_PREFIX = "form-draft:v1:";
const ACTIVE_DRAFT_KEY = "form-draft:v1:active";
/** Abandoned drafts expire; camera-kill recovery is immediate and does not depend on TTL. */
export const DRAFT_TTL_MS = 15 * 60 * 1000;
export const FORM_DRAFT_SCHEMA_VERSION = 1;

export type FormDraftRecord = {
  schemaVersion: number;
  draftKey: string;
  moduleLabel: string;
  route: string;
  sheetId: string;
  entityId?: string | number;
  /** Global property context (`userProperty.id`) at save time — restored on resume. */
  contextPropertyId?: number;
  values: Record<string, unknown>;
  savedAt: number;
};

function getStorage() {
  if (typeof localStorage === "undefined") {
    return null;
  }
  return localStorage;
}

function isStale(savedAt: number) {
  return Date.now() - savedAt > DRAFT_TTL_MS;
}

function draftKeyFromStorageKey(storageKey: string) {
  return storageKey.slice(STORAGE_PREFIX.length);
}

function isDraftRecordStorageKey(key: string) {
  return key.startsWith(STORAGE_PREFIX) && key !== ACTIVE_DRAFT_KEY;
}

function parseDraftRecord(raw: string): FormDraftRecord | null {
  try {
    const record = JSON.parse(raw) as FormDraftRecord;
    if (!record?.draftKey || typeof record.savedAt !== "number") {
      return null;
    }
    if (
      record.schemaVersion != null &&
      record.schemaVersion !== FORM_DRAFT_SCHEMA_VERSION
    ) {
      return null;
    }
    return record;
  } catch {
    return null;
  }
}

/** Remove expired and corrupt draft blobs so localStorage does not accumulate. */
export function purgeStaleFormDrafts() {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  try {
    const keysToRemove: string[] = [];

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key || !isDraftRecordStorageKey(key)) {
        continue;
      }

      const raw = storage.getItem(key);
      if (!raw) {
        keysToRemove.push(key);
        continue;
      }

      const record = parseDraftRecord(raw);
      if (!record || isStale(record.savedAt)) {
        keysToRemove.push(key);
      }
    }

    for (const key of keysToRemove) {
      clearFormDraft(draftKeyFromStorageKey(key));
    }

    const activeKey = storage.getItem(ACTIVE_DRAFT_KEY);
    if (activeKey && !storage.getItem(`${STORAGE_PREFIX}${activeKey}`)) {
      storage.removeItem(ACTIVE_DRAFT_KEY);
    }
  } catch {
    // quota / private mode
  } finally {
    invalidateActiveResumableFormDraftCache();
  }
}

export function saveFormDraft(record: FormDraftRecord) {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  try {
    const previousActive = storage.getItem(ACTIVE_DRAFT_KEY);
    if (previousActive && previousActive !== record.draftKey) {
      clearFormDraft(previousActive);
    }

    storage.setItem(`${STORAGE_PREFIX}${record.draftKey}`, JSON.stringify(record));
    storage.setItem(ACTIVE_DRAFT_KEY, record.draftKey);
    invalidateActiveResumableFormDraftCache();
  } catch {
    // quota / private mode
  }
}

export function loadFormDraft(draftKey: string): FormDraftRecord | null {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(`${STORAGE_PREFIX}${draftKey}`);
    if (!raw) {
      return null;
    }

    const record = parseDraftRecord(raw);
    if (!record || isStale(record.savedAt)) {
      clearFormDraft(draftKey);
      return null;
    }

    return record;
  } catch {
    clearFormDraft(draftKey);
    return null;
  }
}

export function loadLatestFormDraft(): FormDraftRecord | null {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  const draftKey = storage.getItem(ACTIVE_DRAFT_KEY);
  if (!draftKey) {
    return null;
  }

  return loadFormDraft(draftKey);
}

/** Sheet ids that support cold-start recovery (keep in sync with architecture.mdc). */
export const RESUMABLE_FORM_DRAFT_SHEET_IDS = new Set([
  "dar",
  "citation-create",
  "citation-edit",
  "citation-proof",
  "citation-tow-dispatch",
  "citation-tow-cancel",
  "incident",
  "inspection",
  "amenity-access-log-pool",
  "amenity-access-log-tennis",
  "amenity-access-log-gym",
  "amenity-access-log-recreation",
  "dispatch-log",
  "stored-vehicle-create",
  "stored-vehicle-follow-up",
  "stored-vehicle-cancel",
  "patrol-vehicle-condition-start",
  "patrol-vehicle-condition-end",
  "briefing-board",
]);

export function isResumableFormDraftSheetId(sheetId: string) {
  return RESUMABLE_FORM_DRAFT_SHEET_IDS.has(sheetId);
}

let cachedActiveResumableDraft: FormDraftRecord | null | undefined;

function invalidateActiveResumableFormDraftCache() {
  cachedActiveResumableDraft = undefined;
}

export function loadActiveResumableFormDraft(): FormDraftRecord | null {
  if (cachedActiveResumableDraft !== undefined) {
    return cachedActiveResumableDraft;
  }

  const draft = loadLatestFormDraft();
  const result = draft && isResumableFormDraftSheetId(draft.sheetId) ? draft : null;
  cachedActiveResumableDraft = result;
  return result;
}

export function clearFormDraft(draftKey: string) {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(`${STORAGE_PREFIX}${draftKey}`);
    if (storage.getItem(ACTIVE_DRAFT_KEY) === draftKey) {
      storage.removeItem(ACTIVE_DRAFT_KEY);
    }
    invalidateActiveResumableFormDraftCache();
  } catch {
    // ignore
  }
}
